import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Aviso from '@/components/movimento/Aviso';
import type { TipoAviso } from '@/components/movimento/Aviso';
import PainelVagas from '../components/PainelVagas';
import { chaveGrupo, chaveSenha } from '../vagas';
import type { GrupoVagas, SuplenteLinha } from '../types';

interface Props {
  eventoId: number;
  /** Modalidades deste público (campistas). */
  modalidades: number[];
  /** Leva para a lista de pessoas (usado nos alertas). */
  aoAbrirPessoa: () => void;
}

interface AvisoVagas {
  texto: string;
  tipo: TipoAviso;
}

// Tela cheia "Vagas e suplentes" da aba Campistas: o painel de vagas + alertas.
export default function TelaVagasCampistas({ eventoId, modalidades, aoAbrirPessoa }: Props) {
  const [grupos, setGrupos] = useState<GrupoVagas[]>([]);
  const [fila, setFila] = useState<SuplenteLinha[]>([]);
  const [telefones, setTelefones] = useState<Map<number, string>>(new Map());
  const [senhas, setSenhas] = useState<Map<string, number>>(new Map());
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<AvisoVagas | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [reload, setReload] = useState(0);

  // Quem já tinha subido (para saber o que é novidade) e os nomes por inscrição.
  const subiuRef = useRef<Map<number, string | null>>(new Map());
  const nomesRef = useRef<Map<number, string>>(new Map());
  // Só escuta o tempo real depois da 1ª leitura (para não avisar do que já era).
  const prontoRef = useRef(false);

  useEffect(() => {
    if (Number.isNaN(eventoId)) return;
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const [resPainel, resFila, resTel, resSenhas] = await Promise.all([
          supabase.rpc('painel_vagas', { p_evento: eventoId }),
          supabase.rpc('fila_de_suplentes', { p_evento: eventoId }),
          modalidades.length > 0
            ? supabase
                .from('vw_inscritos')
                .select('inscricao_id, participante, participante_telefone')
                .eq('evento_id', eventoId)
                .in('modalidade_id', modalidades)
            : Promise.resolve({ data: [], error: null }),
          modalidades.length > 0
            ? supabase
                .from('inscricoes')
                .select('id, modalidade_id, opcao_id, senha_num, subiu_em, subiu_no_lugar_de')
                .eq('evento_id', eventoId)
                .in('modalidade_id', modalidades)
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (!ativo) return;
        if (resPainel.error) throw resPainel.error;

        const ids = new Set(modalidades.map(Number));
        const todos =
          Array.isArray(resPainel.data) ? (resPainel.data as GrupoVagas[]) : [];
        setGrupos(
          modalidades.length > 0
            ? todos.filter((g) => ids.has(Number(g.modalidade_id)))
            : todos,
        );
        setFila(
          !resFila.error && Array.isArray(resFila.data)
            ? (resFila.data as SuplenteLinha[]).filter((f) =>
                modalidades.length > 0 ? ids.has(Number(f.modalidade_id)) : true,
              )
            : [],
        );

        const mapaTel = new Map<number, string>();
        const mapaNomes = new Map<number, string>();
        ((
          resTel.data as {
            inscricao_id: number;
            participante: string | null;
            participante_telefone: string | null;
          }[]
        ) || []).forEach((l) => {
          if (l.participante_telefone) {
            mapaTel.set(Number(l.inscricao_id), l.participante_telefone);
          }
          if (l.participante) mapaNomes.set(Number(l.inscricao_id), l.participante);
        });
        setTelefones(mapaTel);

        const mapaSenhas = new Map<string, number>();
        const mapaSubiu = new Map<number, string | null>();
        ((
          resSenhas.data as {
            id: number;
            modalidade_id: number;
            opcao_id: number | null;
            senha_num: number | null;
            subiu_em: string | null;
            subiu_no_lugar_de: number | null;
          }[]
        ) || []).forEach((l) => {
          mapaSubiu.set(Number(l.id), l.subiu_em ?? null);
          if (l.senha_num !== null) {
            const chave = chaveSenha(
              chaveGrupo(l.modalidade_id, l.opcao_id),
              l.senha_num,
            );
            mapaSenhas.set(chave, (mapaSenhas.get(chave) ?? 0) + 1);
          }
        });
        setSenhas(mapaSenhas);
        subiuRef.current = mapaSubiu;
        nomesRef.current = mapaNomes;
        prontoRef.current = true;
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível carregar as vagas.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId, modalidades, reload]);

  // Tempo real: quando um suplente sobe sozinho, avisa e relê as duas listas.
  useEffect(() => {
    if (Number.isNaN(eventoId)) return undefined;
    const canal = supabase
      .channel(`vagas-inscricoes-${eventoId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'inscricoes',
          filter: `evento_id=eq.${eventoId}`,
        },
        (payload) => {
          const novo = payload.new as {
            id?: number;
            subiu_em?: string | null;
            subiu_no_lugar_de?: number | null;
          };
          const id = Number(novo?.id);
          if (!prontoRef.current) return;
          if (!id || !novo?.subiu_em) return;
          const antes = subiuRef.current.get(id) ?? null;
          // Só avisa quando o campo passa de vazio para preenchido.
          if (antes) return;
          const nome = nomesRef.current.get(id) ?? 'Uma pessoa';
          const lugarId = novo.subiu_no_lugar_de ? Number(novo.subiu_no_lugar_de) : 0;
          const lugar = lugarId ? nomesRef.current.get(lugarId) ?? null : null;
          subiuRef.current.set(id, novo.subiu_em);
          setAviso({
            texto: lugar
              ? `${nome} subiu para a vaga de ${lugar}.`
              : `${nome} subiu de suplente e agora tem vaga.`,
            tipo: 'sucesso',
          });
          setReload((r) => r + 1);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(canal);
    };
  }, [eventoId]);

  const resumo = useMemo(() => {
    const comVagas = grupos.filter((g) => g.vagas !== null && g.vagas !== undefined);
    return {
      livres: comVagas.reduce((s, g) => s + Number(g.livres ?? 0), 0),
      suplentes: comVagas.reduce((s, g) => s + Number(g.suplentes ?? 0), 0),
      alertas: comVagas.filter((g) => g.alerta_vaga_livre).length,
    };
  }, [grupos]);

  async function darVaga(inscricaoId: number, nome: string) {
    setOcupado(true);
    setAviso(null);
    try {
      const { data } = await supabase.rpc('dar_vaga_ao_suplente', {
        p_inscricao: inscricaoId,
        p_motivo: null,
      });
      const res = data as { ok: boolean; erro?: string } | null;
      setAviso(
        res?.ok
          ? { texto: `${nome} saiu dos suplentes e agora tem vaga.`, tipo: 'sucesso' }
          : { texto: res?.erro ?? 'Não foi possível dar a vaga.', tipo: 'erro' },
      );
      if (res?.ok) setReload((r) => r + 1);
    } catch (e) {
      setAviso({ texto: mensagemAmigavel(e, 'Não foi possível dar a vaga.'), tipo: 'erro' });
    } finally {
      setOcupado(false);
    }
  }

  const fecharAviso = useCallback(() => setAviso(null), []);

  if (carregando) {
    return (
      <div className="space-y-3">
        <div className="h-16 esqueleto rounded-3xl bg-secondary-100" />
        <div className="h-64 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-3xl border border-secondary-200 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => setReload((r) => r + 1)}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const comVagas = grupos.filter((g) => g.vagas !== null && g.vagas !== undefined);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-background-200 bg-white px-4 py-3 text-[13px] text-foreground-700">
        <span>
          <strong className="font-bold text-foreground-950">{resumo.livres}</strong>{' '}
          {resumo.livres === 1 ? 'vaga livre' : 'vagas livres'}
        </span>
        <span className="text-foreground-300">·</span>
        <span>
          <strong className="font-bold text-foreground-950">{resumo.suplentes}</strong>{' '}
          {resumo.suplentes === 1 ? 'suplente' : 'suplentes'}
        </span>
        {resumo.alertas > 0 && (
          <>
            <span className="text-foreground-300">·</span>
            <span className="font-semibold text-amber-800">
              {resumo.alertas} {resumo.alertas === 1 ? 'grupo precisa' : 'grupos precisam'} de
              atenção
            </span>
          </>
        )}
      </div>

      {comVagas.length === 0 ? (
        <div className="rounded-3xl border border-secondary-200 bg-white p-10 text-center">
          <p className="text-[15px] text-foreground-800">
            Este evento não usa vagas por grupo.
          </p>
          <button
            type="button"
            onClick={aoAbrirPessoa}
            className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Ver as pessoas
          </button>
        </div>
      ) : (
        <PainelVagas
          grupos={comVagas}
          fila={fila}
          telefonePorInscricao={telefones}
          contagemSenhas={senhas}
          aoDarVaga={darVaga}
          aoAbrirPessoa={aoAbrirPessoa}
          ocupado={ocupado}
        />
      )}

      <Aviso
        aberto={!!aviso}
        texto={aviso?.texto ?? ''}
        tipo={aviso?.tipo ?? 'neutro'}
        aoFechar={fecharAviso}
      />
    </div>
  );
}